/**
 * The dev-only reel: one arrow press, one whole case of the card.
 *
 * A module's card says one sentence out of a ladder — a hundred finishers, then
 * ninety players, then a friend, then the plain count — and each rung sits behind
 * facts that cannot be produced by hand: a board of a hundred and thirty-seven
 * plays, a seat in a class, a follow, a run that is three capsules in. Reviewing
 * the ladder by setting one `localStorage` key at a time is not reviewing, it is
 * archaeology, so the cases are written down (`#/lib/moduleFixtures`), each one a
 * complete set of answers, and this is the door: two arrows and a counter at the
 * very top of a post's own header, where the thing being switched is on screen
 * below.
 *
 * Four rules kept deliberately:
 *
 *  - **It is never rendered unless `IS_DEV` and preview mode is on**, and every
 *    fact it writes is read behind the same checks on the screens that draw a
 *    module — so a production build has no path to an invented class, a shared URL
 *    cannot make one appear, and the switch in the settings screen
 *    (`#/screens/Settings`) takes the whole reel away when a reviewer wants the real
 *    class instead. That last one is what the second condition is: the tool is
 *    *drawn* only while it is *on*, and the case it was left on is kept for the next
 *    time it is.
 *  - **It changes the *data*, not the screens.** The cases go through the same
 *    one-fact writers the preview bar uses, and the invented plays through the same
 *    aggregator and components the class's own plays do, so what is previewed is
 *    what ships and cannot drift from it.
 *  - **It is loud.** A row that looked like the app would end up in a screenshot
 *    presented as real: this one is dashed and red — the preview bar's own signal —
 *    and it says *PREVIEW* on it.
 *  - **It is a reel, not a settings screen.** ‹ and › move through the cases in the
 *    order a class lives them, and *RESET* puts the reader back on their own class
 *    and their own run in one press, because the moment a preview is in the way is
 *    the moment you want it gone.
 *
 * Removing the feature later is deleting this file, the case table and the stored
 * keys — nothing else reads them.
 */
import type {ReactNode} from 'react'
import {Pressable, StyleSheet, View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {atoms as a, useTheme} from '#/alf'
import {ChevronLeft_Stroke2_Corner0_Rounded as ChevronLeft} from '#/components/icons/Chevron'
import {ChevronRight_Stroke2_Corner0_Rounded as ChevronRight} from '#/components/icons/Chevron'
import {Text} from '#/components/Typography'
import {IS_DEV} from '#/env'
import {useModulePreviewCase} from './useModulePreviewCase'

/** The preview's own ink — the dashed red the bar and the chip already use. */
const PREVIEW_INK = 'rgba(200, 60, 60, 0.9)'

export function ModuleCaseSwitcher({
  testID = 'moduleCaseSwitcher',
}: {
  testID?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const {previewCase, position, total, mode, next, prev, reset} =
    useModulePreviewCase()

  if (!IS_DEV || !mode) return null

  const label = previewCase
    ? l(previewCase.label)
    : l`Off the reel — the real class`

  return (
    <View testID={testID} style={[styles.row, t.atoms.bg_contrast_25]}>
      <Arrow
        testID={`${testID}-prev`}
        label={l`Previous case`}
        onPress={prev}
        icon={<ChevronLeft size="sm" fill={PREVIEW_INK} />}
      />

      {/*
        The counter over the label, both clamped to the middle of the row.

        `w_full` on the label is not decoration: without it a long case name lays
        itself out at its natural width, overflows the column, and gets clipped
        against the arrows on both sides — the ellipsis only happens once the text
        is as narrow as the box it is supposed to fit in.
      */}
      <View style={[a.flex_1, a.align_center]}>
        <Text style={styles.counter}>
          {l`PREVIEW`} · {position || '—'}/{total}
        </Text>
        <Text
          numberOfLines={1}
          style={[a.w_full, a.text_center, styles.label, t.atoms.text]}>
          {label}
        </Text>
      </View>

      <Arrow
        testID={`${testID}-next`}
        label={l`Next case`}
        onPress={next}
        icon={<ChevronRight size="sm" fill={PREVIEW_INK} />}
      />

      <Pressable
        testID={`${testID}-reset`}
        accessibilityRole="button"
        accessibilityLabel={l`Stop previewing and show the real class`}
        onPress={reset}
        style={({pressed}) => [styles.reset, pressed && styles.pressed]}>
        <Text style={styles.resetInk}>{l`RESET`}</Text>
      </Pressable>
    </View>
  )
}

/** One arrow — its own component, so the two cannot drift in size or in label. */
function Arrow({
  testID,
  label,
  onPress,
  icon,
}: {
  testID: string
  label: string
  onPress: () => void
  icon: ReactNode
}) {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({pressed}) => [styles.arrow, pressed && styles.pressed]}>
      {icon}
    </Pressable>
  )
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(200, 60, 60, 0.5)',
    borderStyle: 'dashed',
  },
  arrow: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
  },
  counter: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    color: PREVIEW_INK,
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
  },
  reset: {
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  resetInk: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    color: PREVIEW_INK,
  },
  pressed: {
    opacity: 0.6,
  },
})
